import {
  ChevronUp,
  FileText,
  Maximize2,
  Minimize2,
  Minus,
  Paperclip,
  Search,
  Send,
  Smile,
  UserPlus,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComposeDraftBody, OutgoingAttachment } from '../../api/comms';
import { ApiError } from '../../api/envelope';
import type { ComposeDraft, Conversation, CrmContact, Inbox } from '../../api/types';
import { capabilitiesOf } from '../../lib/channelCapabilities';
import { escapeToHtml, smsCount } from '../../lib/commsFormat';
import { ContactAvatar } from '../../components/ContactAvatar';
import {
  CHANNEL_META,
  channelKeyForInbox,
  inboxCanReach,
  type ChannelKey,
} from '../../lib/commsMeta';
import { identityLine, needsReauth, reauthFixHint } from '../../lib/channelMeta';
import { channelReaching } from '../../lib/composeChannel';
import { EMOJI } from '../../lib/emoji';
import { useDismissOnOutside } from '../../lib/useDismiss';
import { useComms } from './context';
import { dropComposeDraft, upsertComposeDraft } from './composeDrafts';
import { RecipientFields, type RecipientLists } from './RecipientFields';
import { RichText, type RichTextHandle, type RichTextValue } from './RichText';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s().-]{6,}$/;

type Recipient =
  | { kind: 'contact'; contact: CrmContact }
  | { kind: 'raw'; value: string; isEmail: boolean };

/** Docked (bottom-right), minimised to its title bar, or blown up over the module. */
type DockState = 'open' | 'min' | 'max';

/** The order the channel chips are DRAWN in. Which one gets chosen when several
 *  fit is a different question with a different order — see composeChannel.ts. */
const COMPOSE_ORDER: ChannelKey[] = ['email', 'whatsapp', 'sms', 'telegram'];

function contactSub(contact: CrmContact): string {
  return [contact.email, contact.phoneNumber].filter(Boolean).join(' · ') || '—';
}

/**
 * A recipient decided elsewhere: the dashboard named a person by address and
 * the module found no conversation to open, so the compose window opens on
 * them instead of on an empty search box.
 */
export interface ComposeSeed {
  address: string;
  isEmail: boolean;
  /** The channel the icon meant — email, sms, whatsapp… */
  channelKey: ChannelKey;
  /** The CRM contact who owns the address, when one was found. */
  contact: CrmContact | null;
}

/** What `inboxCanReach` needs to know about a recipient, whoever they are. */
const shapeOf = (recipient: Recipient) =>
  recipient.kind === 'contact'
    ? { email: recipient.contact.email, phoneNumber: recipient.contact.phoneNumber }
    : {
        email: recipient.isEmail ? recipient.value : null,
        phoneNumber: recipient.isEmail ? null : recipient.value,
      };

const seedRecipient = (seed: ComposeSeed | undefined): Recipient | null => {
  if (!seed) return null;
  return seed.contact
    ? { kind: 'contact', contact: seed.contact }
    : { kind: 'raw', value: seed.address, isEmail: seed.isEmail };
};

/**
 * The inbox a seeded panel opens on: the mail icon means email even for a
 * customer who is also reachable on WhatsApp, so the channel the agent pressed
 * decides — not the first one that happens to reach them.
 *
 * Resolved at mount rather than in an effect, which it can be because a seed
 * only ever arrives once the inboxes have loaded (see useReach). Null for a
 * call, which names no channel of its own, and for a channel that cannot reach
 * this recipient — the panel then asks, as it does for any other new message.
 */
const seedInbox = (seed: ComposeSeed | undefined, inboxes: Inbox[]): Inbox | null => {
  if (!seed || seed.channelKey === 'all') return null;
  const shape = seed.contact
    ? { email: seed.contact.email, phoneNumber: seed.contact.phoneNumber }
    : {
        email: seed.isEmail ? seed.address : null,
        phoneNumber: seed.isEmail ? null : seed.address,
      };
  return (
    inboxes.find(
      (inbox) => channelKeyForInbox(inbox) === seed.channelKey && inboxCanReach(inbox, shape),
    ) ?? null
  );
};

/**
 * The channel the panel opens on, and the inbox under it.
 *
 * A seed wins, because an icon in the dashboard named a channel outright. With
 * no seed the agent's CURRENT TAB is the answer: somebody who presses Compose
 * while standing in Email means email, and asking them to say so again is a
 * click that has only one right answer. It is a starting point and not a lock —
 * picking a recipient the channel cannot reach moves it (see chooseRecipient).
 *
 * Null on the All tab, on Calls and on the website channel, which cannot start
 * a conversation: those genuinely have no answer, and guessing one would send
 * mail from a mailbox nobody chose.
 */
const openingChannel = (
  seed: ComposeSeed | undefined,
  inboxes: Inbox[],
  activeChannelKey: ChannelKey | undefined,
): { key: ChannelKey; inbox: Inbox } | null => {
  const seeded = seedInbox(seed, inboxes);
  if (seeded && seed) return { key: seed.channelKey, inbox: seeded };
  // A seed whose channel cannot reach its recipient asks, rather than falling
  // through to the tab — the icon was specific and answering it with something
  // else is worse than answering it with a question.
  if (seed) return null;
  if (!activeChannelKey || !COMPOSE_ORDER.includes(activeChannelKey)) return null;
  const inbox = inboxes.find((one) => channelKeyForInbox(one) === activeChannelKey);
  return inbox ? { key: activeChannelKey, inbox } : null;
};

/**
 * How long the panel waits after the last keystroke before saving. Long enough
 * that a sentence is one write rather than thirty, short enough that "Draft
 * saved" appears while the agent is still looking at the box that earned it.
 */
const DRAFT_SAVE_DEBOUNCE_MS = 900;

/** The recipient a stored draft was addressed to, in the panel's own shape. */
const draftRecipient = (draft: ComposeDraft | undefined): Recipient | null => {
  if (!draft) return null;
  if (draft.contact) return { kind: 'contact', contact: draft.contact };
  return draft.toAddress
    ? { kind: 'raw', value: draft.toAddress, isEmail: draft.toIsEmail }
    : null;
};

function fileType(file: File): OutgoingAttachment['fileType'] {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('video/')) return 'video';
  return 'file';
}

/**
 * New message, docked bottom-right like a mail client's compose window rather
 * than a modal. That is the whole point of the shape: the module stays live
 * behind it, so an agent can minimise the draft, open another conversation to
 * copy a quote or a booking reference out of it, and come back to the same
 * half-written message. A modal made that a choice between the two.
 *
 * The panel therefore must never unmount while it is open — `min` only stops
 * rendering the body.
 *
 * Once a RECIPIENT is named, what is typed here is also kept as a draft on the
 * server, the same way a half-written reply is (`compose_drafts`). The recipient
 * is what switches that on and it is not an arbitrary line: a draft has to be
 * reopenable ONTO somebody, and a body addressed to nobody is one the agent
 * would find in the Drafts view with no way to finish it.
 */
export function ComposePanel({
  onClose,
  onCreated,
  seed,
  activeChannelKey,
  draft,
}: {
  onClose: () => void;
  onCreated: (conversation: Conversation, channelKey: ChannelKey) => void;
  /**
   * Opened FOR someone — a contact icon in the dispatch dashboard that found
   * no thread to open. The recipient is already decided, so the panel skips
   * its search box and starts on the channel that icon meant.
   */
  seed?: ComposeSeed;
  /** The channel tab the module is on, which is what Send via opens set to. */
  activeChannelKey?: ChannelKey;
  /**
   * Reopened ON a stored draft, from the Drafts view. Everything below is
   * seeded from it and every later save PATCHES it — the row the agent clicked
   * is the row they carry on writing, never a second one beside it.
   */
  draft?: ComposeDraft;
}) {
  const { source, inboxes, templateBlocks, sendDenied, noteSendDenied, canReply } = useComms();
  const [dock, setDock] = useState<DockState>('open');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CrmContact[]>([]);
  const [searching, setSearching] = useState(false);
  // Frozen at mount: a seed or a stored draft arriving later must not overwrite
  // what the agent is halfway through. A REOPENED draft wins over a seed and
  // over the tab — it is the most specific request of the three, and it names an
  // account the agent already chose once.
  const draftInbox = draft ? (inboxes.find((inbox) => inbox.id === draft.inboxId) ?? null) : null;
  const [recipient, setRecipient] = useState<Recipient | null>(
    () => draftRecipient(draft) ?? seedRecipient(seed),
  );
  // Both frozen at mount for the same reason the recipient is: the tab can
  // change behind an open panel, and moving the draft's channel under the agent
  // would be worse than it being one tab out of date.
  const [channelKey, setChannelKey] = useState<ChannelKey | null>(() =>
    draftInbox
      ? channelKeyForInbox(draftInbox)
      : (openingChannel(seed, inboxes, activeChannelKey)?.key ?? null),
  );
  const [inboxId, setInboxId] = useState<number | null>(
    () => draftInbox?.id ?? openingChannel(seed, inboxes, activeChannelKey)?.inbox.id ?? null,
  );
  /** Cc and Bcc for an email. `to` is the picked recipient and is never used. */
  const [extras, setExtras] = useState<RecipientLists>(() => ({
    to: draft?.toEmails ?? [],
    cc: draft?.ccEmails ?? [],
    bcc: draft?.bccEmails ?? [],
  }));
  // Open when a restored draft has something in them, or the agent would have
  // to guess that the copies they typed yesterday are still on the message.
  const [extrasOpen, setExtrasOpen] = useState(
    () => (draft?.ccEmails.length ?? 0) + (draft?.bccEmails.length ?? 0) > 0,
  );
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [text, setText] = useState(() =>
    draftInbox && !capabilitiesOf(draftInbox).richText ? (draft?.content ?? '') : '',
  );
  const [rich, setRich] = useState<RichTextValue>(() =>
    draftInbox && capabilitiesOf(draftInbox).richText
      ? { html: draft?.bodyHtml ?? escapeToHtml(draft?.content ?? ''), text: draft?.content ?? '' }
      : { html: '', text: '' },
  );
  const [attachments, setAttachments] = useState<OutgoingAttachment[]>(draft?.attachments ?? []);
  /**
   * The server row this panel is writing into, once there is one. Allocated by
   * the first save rather than at mount, so a window opened and closed without
   * a word in it leaves nothing behind.
   */
  const [draftId, setDraftId] = useState<number | null>(draft?.id ?? null);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(draft?.updatedAt ?? null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useDismissOnOutside<HTMLDivElement>(emojiOpen, () => setEmojiOpen(false));
  const [sending, setSending] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const bodyInput = useRef<HTMLTextAreaElement>(null);
  const bodyEditor = useRef<RichTextHandle>(null);
  const [blocksOpen, setBlocksOpen] = useState(false);
  const blocksRef = useDismissOnOutside<HTMLDivElement>(blocksOpen, () => setBlocksOpen(false));

  // Debounced contact search (server-side q on name/email/phone).
  useEffect(() => {
    if (recipient) return;
    let cancelled = false;
    // The spinner must flip with the keystroke that scheduled the search.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearching(true);
    const timer = setTimeout(() => {
      source.api.listContacts(query.trim()).then(
        (rows) => {
          if (cancelled) return;
          setResults(rows);
          setSearching(false);
        },
        () => {
          if (cancelled) return;
          setResults([]);
          setSearching(false);
        },
      );
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, recipient, source]);

  const trimmed = query.trim();
  const rawIsEmail = EMAIL_RE.test(trimmed);
  const rawIsPhone = !rawIsEmail && PHONE_RE.test(trimmed);
  const showRawOption = results.length === 0 && !searching && (rawIsEmail || rawIsPhone);

  /**
   * The channel a typed address can actually be sent on. An address decides its
   * own kind — a phone number is not emailable however the panel is set — so
   * this keeps the selected channel only when it fits, and otherwise names the
   * one that does.
   */
  const rawChannel = useMemo<ChannelKey | null>(() => {
    if (!showRawOption) return null;
    return (
      channelReaching(
        inboxes,
        { email: rawIsEmail ? trimmed : null, phoneNumber: rawIsEmail ? null : trimmed },
        channelKey,
      )?.key ?? null
    );
  }, [showRawOption, rawIsEmail, trimmed, inboxes, channelKey]);

  /** The typed address needs a channel other than the one that is selected. */
  const rawSwitches = rawChannel !== null && channelKey !== null && rawChannel !== channelKey;
  // Capitalised and hoisted because JSX cannot name an element by a computed
  // member — `<CHANNEL_META[key].icon />` is a syntax error, not a lookup.
  const RawChannelIcon = rawChannel ? CHANNEL_META[rawChannel].icon : UserPlus;

  const recipientShape = useMemo(() => {
    if (!recipient) return null;
    if (recipient.kind === 'contact') {
      return {
        name:
          recipient.contact.name ||
          recipient.contact.email ||
          recipient.contact.phoneNumber ||
          'Unknown',
        sub: contactSub(recipient.contact),
        email: recipient.contact.email,
        phoneNumber: recipient.contact.phoneNumber,
      };
    }
    return {
      name: recipient.value,
      sub: 'New contact — created on send.',
      email: recipient.isEmail ? recipient.value : null,
      phoneNumber: recipient.isEmail ? null : recipient.value,
    };
  }, [recipient]);

  // Which channel groups can reach this recipient, and through which inboxes.
  // Only the channels this agent may SEND on, like the reply box: offering a
  // read-only line here lit Send on a message the server was going to refuse.
  const reachable = useMemo(() => {
    const map = new Map<ChannelKey, Inbox[]>();
    if (!recipientShape) return map;
    for (const inbox of inboxes) {
      if (!canReply(inbox.id) || !inboxCanReach(inbox, recipientShape)) continue;
      const key = channelKeyForInbox(inbox);
      map.set(key, [...(map.get(key) ?? []), inbox]);
    }
    return map;
  }, [inboxes, recipientShape, canReply]);

  /** Every channel the tenant has connected that this agent may write on, whoever is being written to. */
  const connected = useMemo(() => {
    const map = new Map<ChannelKey, Inbox[]>();
    for (const inbox of inboxes) {
      if (!canReply(inbox.id)) continue;
      const key = channelKeyForInbox(inbox);
      map.set(key, [...(map.get(key) ?? []), inbox]);
    }
    return map;
  }, [inboxes, canReply]);

  /**
   * What Send via offers. Before a recipient is named it is everything that is
   * connected — the row sits above the search box now, so it has to answer with
   * something before there is anyone to reach. Once somebody is named it
   * narrows to the channels that can actually reach them.
   */
  const offered = recipient ? reachable : connected;

  /**
   * The styled editor and the plain textarea hold separate state, so moving
   * between an email inbox and a chat one has to carry the draft across or the
   * switch looks like it swallowed what was typed.
   */
  const selectInbox = (next: Inbox) => {
    const from = capabilitiesOf(inboxes.find((inbox) => inbox.id === inboxId));
    const to = capabilitiesOf(next);
    if (from.richText && !to.richText) setText(rich.text);
    if (!from.richText && to.richText) setRich({ html: escapeToHtml(text), text });
    setInboxId(next.id);
  };

  const pickChannel = (key: ChannelKey) => {
    const candidates = offered.get(key);
    if (!candidates || candidates.length === 0) return;
    setChannelKey(key);
    selectInbox(candidates[0]);
  };

  /**
   * Name the recipient, and keep the channel honest about reaching them.
   *
   * Send via now opens already set — to the tab the agent was standing on — so
   * the panel can be pointed at Email and then handed a phone number. The
   * channel yields rather than the recipient: a person is a fact, and "Email"
   * was a default nobody typed. `preferred` is the switch button saying which
   * one the agent just chose on purpose.
   */
  const chooseRecipient = (next: Recipient, preferred?: ChannelKey) => {
    setRecipient(next);
    const found = channelReaching(inboxes, shapeOf(next), preferred ?? channelKey);
    if (!found) {
      // Nothing reaches them; the Send via row says so rather than pretending.
      setChannelKey(null);
      setInboxId(null);
      return;
    }
    setChannelKey(found.key);
    selectInbox(found.inbox);
  };

  const selectedInbox = inboxes.find((inbox) => inbox.id === inboxId) ?? null;
  // The controls a composer may offer are the channel's, not the screen's — the
  // same table the reply box reads, so the two cannot disagree about what a
  // channel can carry.
  const caps = capabilitiesOf(selectedInbox ?? undefined);
  const body = caps.richText ? rich.text : text;
  const isSms = channelKey === 'sms';
  const count = isSms ? smsCount(body.length) : null;
  /** Email addresses a header; every other channel addresses one person. */
  const mailHeader = caps.cc || caps.subject;
  // Extra To lines count with the copies: they are the same promise (this
  // message reaches more than one person) and the same channels refuse them.
  const copies = extras.to.length + extras.cc.length + extras.bcc.length;
  const hasDraft =
    body.trim().length > 0 || attachments.length > 0 || subject.trim().length > 0 || copies > 0;
  // Switching the sending account can move between a channel that carries files
  // and one that does not (SMS is Twilio or RingCentral). Say so and block the
  // send rather than dropping the files or shipping a message without them.
  const orphanedAttachments = selectedInbox !== null && !caps.attachments && attachments.length > 0;
  // The same rule for Cc and Bcc, and for the same reason: only email carries
  // them, so moving to SMS with somebody copied in would drop them in silence.
  const orphanedCopies = selectedInbox !== null && !mailHeader && copies > 0;
  /**
   * The account this would send from lost its authorization upstream. Unlike a
   * reply, this send also CREATES the conversation — so letting it through would
   * open a thread whose only message is a failure, on a channel that cannot
   * carry the retry either. The picker below still lists the account: it is the
   * right sender again the moment somebody reconnects it.
   */
  const staleInbox = selectedInbox !== null && needsReauth(selectedInbox);
  const canSend =
    !sendDenied &&
    recipient !== null &&
    selectedInbox !== null &&
    (body.trim().length > 0 || attachments.length > 0) &&
    !orphanedAttachments &&
    !orphanedCopies &&
    !staleInbox &&
    !uploading &&
    !sending;

  /**
   * What a save would store, or null when there is nothing worth keeping.
   *
   * Derived rather than remembered, so a debounce that fires late always writes
   * the box as it is NOW — a remembered snapshot would store the sentence before
   * the last one. Null before a recipient is named (see the docblock), and null
   * for an empty box, which is what makes emptying the composer delete the row.
   *
   * The copies deliberately do not count as content, matching the reply draft:
   * three addresses over an empty box is not work in progress. They ride along
   * once there IS something, because a draft whose body comes back and whose Cc
   * list does not reads as complete and goes out to one person.
   */
  const draftBody = useMemo<ComposeDraftBody | null>(() => {
    if (!recipient || !selectedInbox) return null;
    const hasSubject = caps.subject && subject.trim().length > 0;
    if (!body.trim() && attachments.length === 0 && !hasSubject) return null;
    return {
      inboxId: selectedInbox.id,
      ...(recipient.kind === 'contact'
        ? { contactId: recipient.contact.id }
        : { toAddress: recipient.value, toIsEmail: recipient.isEmail }),
      subject: hasSubject ? subject.trim() : null,
      content: body,
      bodyHtml: caps.richText && rich.text.trim() ? rich.html : null,
      attachments,
      // Only on a channel that has a header. Everywhere else they are stale
      // leftovers from before the channel moved, and restoring them would put
      // addresses back on a message that cannot carry them.
      ...(mailHeader ? { toEmails: extras.to, ccEmails: extras.cc, bccEmails: extras.bcc } : {}),
    };
  }, [
    recipient,
    selectedInbox,
    body,
    attachments,
    subject,
    rich,
    extras,
    mailHeader,
    caps.subject,
    caps.richText,
  ]);

  /**
   * The row id lives in a ref as well as in state because the save chain reads
   * it: by the time the second link runs, a `setState` from the first has not
   * landed, and a stale null there is how one message becomes two drafts.
   */
  const draftIdRef = useRef<number | null>(draftId);
  const savingRef = useRef<Promise<void>>(Promise.resolve());
  /**
   * True once this panel has been settled deliberately — sent, saved on the way
   * out, or discarded. Anything ELSE that unmounts the dock (reopening a
   * different draft into it) has to leave the words where the agent can find
   * them, so the flush below runs; after a discard it must not, or the message
   * they just threw away would come back.
   */
  const settledRef = useRef(false);
  const draftBodyRef = useRef<ComposeDraftBody | null>(null);
  useEffect(() => {
    draftBodyRef.current = draftBody;
  });

  const writeDraft = useCallback(
    async (payload: ComposeDraftBody | null) => {
      const id = draftIdRef.current;
      // An empty box with nothing stored is not a state to report — otherwise
      // every keystroke in a blank composer would be a request saying so.
      if (!payload && id === null) return;
      setDraftSaving(true);
      try {
        if (!payload) {
          await source.api.deleteComposeDraft(id as number);
          dropComposeDraft(id as number);
          draftIdRef.current = null;
          setDraftId(null);
          setDraftSavedAt(null);
          return;
        }
        const saved =
          id === null
            ? await source.api.createComposeDraft(payload)
            : await source.api.saveComposeDraft(id, payload);
        if (!saved) {
          // The server judged the box empty. Its rule wins — it is the one that
          // decides what the Drafts view holds — so forget the row rather than
          // keep patching one that is gone.
          if (id !== null) dropComposeDraft(id);
          draftIdRef.current = null;
          setDraftId(null);
          setDraftSavedAt(null);
          return;
        }
        draftIdRef.current = saved.id;
        setDraftId(saved.id);
        setDraftSavedAt(saved.updatedAt);
        // The Drafts view redraws from this rather than on its next list read,
        // so a draft appears the moment it is first saved and moves up the list
        // as it is edited.
        upsertComposeDraft(saved);
      } finally {
        setDraftSaving(false);
      }
    },
    [source],
  );

  /**
   * One save in flight at a time. The FIRST one allocates the row, so two of
   * them racing — a fast typist and a slow network — would leave the agent with
   * two drafts of one message.
   */
  const persistDraft = useCallback(
    (payload: ComposeDraftBody | null) => {
      const run = savingRef.current.then(() => writeDraft(payload));
      // The chain has to survive a failed link, or one dropped request would
      // stop every later save silently. The returned promise still rejects, so
      // the close path — the one caller that can act on it — still hears.
      savingRef.current = run.catch(() => {});
      return run;
    },
    [writeDraft],
  );

  /**
   * Autosave, debounced. Held off while a send is in flight: that send is about
   * to delete the row, and a save landing behind it would put it back.
   */
  useEffect(() => {
    if (!recipient || sending) return;
    if (!draftBody && draftIdRef.current === null) return;
    const timer = setTimeout(() => {
      void persistDraft(draftBody).catch(() => {
        // Nothing to say mid-sentence: the next keystroke schedules another
        // attempt, and the close path reports a failure where it costs
        // something — there, the panel stays open rather than losing the words.
      });
    }, DRAFT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draftBody, recipient, sending, persistDraft]);

  /**
   * The last word on the way out — for the exits that are not a button: the
   * dock being handed to a different draft. Everything it reads is a ref, so
   * the only dependency is the stable saver and the cleanup runs once, at
   * unmount, rather than after every keystroke.
   */
  useEffect(() => {
    return () => {
      if (settledRef.current) return;
      // Only ever a save, never a delete. A window whose recipient was cleared
      // with Change still holds words, and reading "nothing to store right now"
      // as "throw the stored row away" would discard a draft on the agent's
      // behalf — which is the one thing this whole path exists to prevent.
      const payload = draftBodyRef.current;
      if (!payload) return;
      void persistDraft(payload).catch(() => {
        // Nothing left to tell: the panel is already gone.
      });
    };
  }, [persistDraft]);

  const resetRecipient = () => {
    setRecipient(null);
    setChannelKey(null);
    setInboxId(null);
  };

  /**
   * Closing asks first, and the default answer keeps the work: a message with a
   * recipient can be put down as a draft and picked up from the Drafts view,
   * which is the whole point of the recipient being the line.
   */
  const requestClose = () => {
    if (hasDraft && !sending) {
      setDock('open');
      setConfirmDiscard(true);
      return;
    }
    // A stored draft emptied by hand and closed inside the save debounce: the
    // delete the debounce owed is sent now, or the old words stay in the
    // Drafts view and come back on the next click. Through the chain, so it
    // cannot overtake a save still on the wire.
    if (draftIdRef.current !== null && !draftBody) {
      settledRef.current = true;
      void persistDraft(null).catch(() => {});
    }
    onClose();
  };

  /** Flush what is in the box, then close — and stay open if the flush failed. */
  const saveAndClose = async () => {
    setError(null);
    try {
      await persistDraft(draftBody);
      settledRef.current = true;
      onClose();
    } catch (e) {
      setConfirmDiscard(false);
      setError(e instanceof ApiError ? e.message : 'Could not save the draft — try again.');
    }
  };

  /**
   * Throw it away, and close without waiting for the server to agree. Two
   * things make that safe: the delete rides the same chain as every save, so a
   * create still in flight cannot land behind it and leave the row it was asked
   * to throw away; and a delete that fails simply leaves the draft in the list,
   * which is the right way round for a failure nobody is watching.
   */
  const discardAndClose = () => {
    settledRef.current = true;
    void persistDraft(null).catch(() => {
      // The Drafts view's next read is the correction.
    });
    onClose();
  };

  const pickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const { s3Key, putUrl } = await source.api.presignUpload(
          file.name,
          file.type || 'application/octet-stream',
        );
        await source.api.putToS3(putUrl, file);
        setAttachments((current) => [
          ...current,
          {
            s3Key,
            fileName: file.name,
            fileType: fileType(file),
            fileSize: file.size,
            extension: file.name.split('.').pop() ?? '',
          },
        ]);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upload failed — try again.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  /** Drop an emoji at the caret rather than at the end of whatever is typed. */
  const insertEmoji = (emoji: string) => {
    setEmojiOpen(false);
    const node = bodyInput.current;
    if (!node) {
      setText((current) => current + emoji);
      return;
    }
    const { selectionStart, selectionEnd } = node;
    setText((current) => current.slice(0, selectionStart) + emoji + current.slice(selectionEnd));
    requestAnimationFrame(() => {
      node.focus();
      node.setSelectionRange(selectionStart + emoji.length, selectionStart + emoji.length);
    });
  };

  const doSend = async () => {
    if (!canSend || !recipient || !selectedInbox) return;
    setSending(true);
    setError(null);
    try {
      let contactId: number;
      if (recipient.kind === 'contact') {
        contactId = recipient.contact.id;
      } else {
        const created = await source.api.createContact(
          recipient.isEmail ? { email: recipient.value } : { phoneNumber: recipient.value },
        );
        contactId = created.id;
        // A retry after a later failure must not try to create this contact
        // again — the unique index would answer 1003 instead of sending.
        setRecipient({ kind: 'contact', contact: created });
      }
      const conversation = await source.api.createConversation({
        inboxId: selectedInbox.id,
        contactId,
        message: {
          content: body.trim(),
          ...(caps.subject && subject.trim() ? { subject: subject.trim() } : {}),
          ...(caps.richText && rich.text.trim() ? { bodyHtml: rich.html } : {}),
          ...(attachments.length ? { attachments } : {}),
          // Same shape the reply composer sends, so one server path validates
          // both. An empty list is left OUT rather than sent as [] — see
          // prepareOutgoingBody on why the difference matters.
          ...(mailHeader && copies
            ? {
                contentAttributes: {
                  // Only when somebody was ADDED. Left out, the server addresses
                  // the contact itself — and a stale copy of their address typed
                  // here should never outrank the contact record.
                  ...(extras.to.length && recipientShape?.email
                    ? { toEmails: [recipientShape.email, ...extras.to] }
                    : {}),
                  ...(extras.cc.length ? { ccEmails: extras.cc } : {}),
                  ...(extras.bcc.length ? { bccEmails: extras.bcc } : {}),
                },
              }
            : {}),
        },
      });
      settledRef.current = true;
      // Through the same chain as every save, not beside it: a delete that
      // overtook an in-flight save would leave the row behind, and the agent
      // would find the message they just sent sitting in Drafts.
      void persistDraft(null).catch(() => {
        // The row is gone from the view already; a stale one corrects itself on
        // the next list read, and the send has succeeded either way.
      });
      onCreated(conversation, channelKeyForInbox(selectedInbox));
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'forbidden') noteSendDenied();
      setError(e instanceof ApiError ? e.message : 'Could not start the conversation.');
      setSending(false);
    }
  };

  const meta = channelKey ? CHANNEL_META[channelKey] : null;
  const draftState = draftSaving
    ? 'Saving draft…'
    : draftId !== null && draftSavedAt
      ? 'Draft saved'
      : null;
  const title = recipientShape
    ? subject.trim() || `New message · ${recipientShape.name}`
    : 'New message';
  const footerNote = sendDenied
    ? 'Your role can view but not send.'
    : !recipient
      ? 'Pick who this goes to.'
      : !selectedInbox
        ? 'Pick a channel that can reach them.'
        : staleInbox
          ? `${identityLine(selectedInbox)} needs reconnecting — it lost its authorization and cannot send. ${reauthFixHint(selectedInbox)}`
          : orphanedAttachments
            ? `${identityLine(selectedInbox)} cannot send attachments — remove them or pick another account.`
            : orphanedCopies
              ? `${CHANNEL_META[channelKey ?? 'email'].label} cannot copy anyone in — clear Cc and Bcc, or send this by email.`
              : `Sends from ${identityLine(selectedInbox)} and opens as a thread in the inbox.`;

  return (
    <div className={`cdock cdock-${dock}`} role="dialog" aria-label="New message">
      <div
        className="cdock-head"
        onClick={dock === 'min' ? () => setDock('open') : undefined}
        role={dock === 'min' ? 'button' : undefined}
        tabIndex={dock === 'min' ? 0 : undefined}
        onKeyDown={(e) => {
          if (dock === 'min' && (e.key === 'Enter' || e.key === ' ')) setDock('open');
        }}
      >
        <div className="cdock-head-icon">
          <Send size={13} />
        </div>
        <div className="cdock-head-title" title={title}>
          {title}
        </div>
        {attachments.length > 0 && dock === 'min' && (
          <span className="cdock-head-clip">
            <Paperclip size={11} />
            {attachments.length}
          </span>
        )}
        <button
          type="button"
          className="cdock-btn"
          title={dock === 'min' ? 'Restore' : 'Minimise'}
          onClick={(e) => {
            e.stopPropagation();
            setDock(dock === 'min' ? 'open' : 'min');
          }}
        >
          {dock === 'min' ? <ChevronUp size={14} /> : <Minus size={14} />}
        </button>
        <button
          type="button"
          className="cdock-btn"
          title={dock === 'max' ? 'Exit full screen' : 'Full screen'}
          onClick={(e) => {
            e.stopPropagation();
            setDock(dock === 'max' ? 'open' : 'max');
          }}
        >
          {dock === 'max' ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
        </button>
        <button
          type="button"
          className="cdock-btn"
          title="Close"
          onClick={(e) => {
            e.stopPropagation();
            requestClose();
          }}
        >
          <X size={14} />
        </button>
      </div>

      {dock !== 'min' && (
        <>
          {confirmDiscard && (
            <div className="cdock-confirm">
              {/* Two different questions, and the difference is the recipient.
                  With one, this is work that can be put down and picked up; with
                  none, there is nowhere to put it down TO — a draft addressed to
                  nobody could not be reopened onto anyone. */}
              <span>{draftBody ? 'Keep this as a draft?' : 'Discard this message?'}</span>
              <button type="button" className="cbtn cbtn-sm" onClick={() => setConfirmDiscard(false)}>
                Keep writing
              </button>
              {draftBody && (
                <button
                  type="button"
                  className="cbtn cbtn-sm cbtn-primary"
                  onClick={() => void saveAndClose()}
                  disabled={draftSaving}
                >
                  {draftSaving ? 'Saving…' : 'Save draft'}
                </button>
              )}
              <button type="button" className="cdock-discard" onClick={discardAndClose}>
                Discard
              </button>
            </div>
          )}

          <div className="cdock-scroll">
            {/* Send via comes FIRST, before the recipient. It used to sit under
                the search box and only appear once somebody had been picked,
                which put the one question with a sensible default behind the
                one without: an agent standing in Email had to name a person
                before the panel would admit it already knew the channel. Above
                it, it opens already set (see openingChannel) and the search
                below narrows it rather than the other way round. */}
            <div className="compose-section">
              <div className="compose-label">SEND VIA</div>
              <div className="compose-channels">
                {COMPOSE_ORDER.map((key) => {
                  const channelMeta = CHANNEL_META[key];
                  const Icon = channelMeta.icon;
                  const available = (offered.get(key)?.length ?? 0) > 0;
                  const active = channelKey === key;
                  const title =
                    key === 'telegram' && !available
                      ? 'Telegram bots cannot start a chat — the contact must message first'
                      : available
                        ? `Send via ${channelMeta.label}`
                        : recipient
                          ? `No ${channelMeta.label} address on file for this recipient`
                          : `No ${channelMeta.label} channel is connected`;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`compose-channel${active ? ' on' : ''}${available ? '' : ' unavailable'}`}
                      title={title}
                      onClick={() => available && pickChannel(key)}
                    >
                      <Icon
                        size={14}
                        style={available && !active ? { color: channelMeta.color } : undefined}
                      />
                      {channelMeta.label}
                    </button>
                  );
                })}
              </div>
              {channelKey && (offered.get(channelKey)?.length ?? 0) > 1 && (
                <select
                  className="compose-template"
                  style={{ width: '100%', flexBasis: 'auto' }}
                  value={inboxId ?? undefined}
                  onChange={(e) => {
                    const next = inboxes.find((inbox) => inbox.id === Number(e.target.value));
                    if (next) selectInbox(next);
                  }}
                  title="Which account to send from"
                >
                  {(offered.get(channelKey) ?? []).map((inbox) => (
                    <option key={inbox.id} value={inbox.id}>
                      {identityLine(inbox)} · {inbox.name}
                      {needsReauth(inbox) ? ' · needs reconnecting' : ''}
                    </option>
                  ))}
                </select>
              )}
              <div className="compose-hint">
                {recipient && offered.size === 0
                  ? 'No connected channel can reach this recipient.'
                  : recipient
                    ? 'Greyed channels have no address on file for this recipient.'
                    : 'Greyed channels are not connected. Picking a recipient below narrows this.'}
              </div>
            </div>

            <div className="compose-section">
              {/* Not "TO" once the header block is showing: that block's first
                  row is already labelled To, and two of them one above the
                  other reads as two fields. */}
              <div className="compose-label">
                {mailHeader && recipient ? 'RECIPIENTS' : 'TO'}
              </div>
              {recipient && recipientShape ? (
                // An email header is a list of addresses, so on email the picked
                // contact becomes the first chip of a To line that takes more.
                // Every other channel addresses exactly one person, so it keeps
                // the card — which says more about who was picked, and there is
                // nothing to add beside them.
                mailHeader ? null : (
                  <div className="compose-picked">
                    {/* No `email` on purpose: a Gravatar lookup per search result
                        would send a hash of every address the agent scrolls past. */}
                    <ContactAvatar
                      name={recipientShape.name}
                      size={30}
                    />
                    <div className="compose-picked-main">
                      <div className="compose-picked-name">{recipientShape.name}</div>
                      <div className="compose-picked-sub">{recipientShape.sub}</div>
                    </div>
                    <button type="button" className="cbtn cbtn-sm" onClick={resetRecipient}>
                      Change
                    </button>
                  </div>
                )
              ) : (
                <>
                  <div className="compose-search">
                    <Search size={14} />
                    <input
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search contacts, or type a number or email address"
                    />
                  </div>
                  <div className="compose-results">
                    {results.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        className="compose-result"
                        onClick={() => chooseRecipient({ kind: 'contact', contact })}
                      >
                        <ContactAvatar
                          name={contact.name || contact.email || contact.phoneNumber || '?'}
                          size={28}
                        />
                        <div className="compose-result-main">
                          <div className="compose-result-toprow">
                            <span className="compose-result-name">
                              {contact.name || contact.email || contact.phoneNumber}
                            </span>
                          </div>
                          <div className="compose-result-sub">{contactSub(contact)}</div>
                        </div>
                        <div className="compose-result-marks">
                          {contact.email && <CHANNEL_META.email.icon size={13} />}
                          {contact.phoneNumber && <CHANNEL_META.sms.icon size={13} />}
                        </div>
                      </button>
                    ))}
                    {showRawOption && (
                      <button
                        type="button"
                        className={`compose-result raw${rawSwitches ? ' switches' : ''}`}
                        onClick={() =>
                          chooseRecipient(
                            { kind: 'raw', value: trimmed, isEmail: rawIsEmail },
                            rawChannel ?? undefined,
                          )
                        }
                      >
                        {rawSwitches && rawChannel ? (
                          <RawChannelIcon
                            size={15}
                            style={{ color: CHANNEL_META[rawChannel].color }}
                          />
                        ) : (
                          <UserPlus size={15} />
                        )}
                        <div className="compose-result-main">
                          <div className="compose-result-name">
                            {/* An address decides its own channel. Rather than
                                greying this row out for being the wrong kind
                                for the tab the panel opened on, it offers the
                                move — the agent typed a person, not a channel. */}
                            {rawSwitches && rawChannel
                              ? `Switch to ${CHANNEL_META[rawChannel].label} and message ${trimmed}`
                              : rawIsEmail
                                ? `Email ${trimmed}`
                                : `Message ${trimmed}`}
                          </div>
                          <div className="compose-result-sub">
                            {rawSwitches && channelKey
                              ? `${rawIsEmail ? 'An email address' : 'A phone number'} — ${CHANNEL_META[channelKey].label} cannot reach it. Not in contacts, so a new contact record is created on send.`
                              : 'Not in contacts — a new contact record is created on send.'}
                          </div>
                        </div>
                      </button>
                    )}
                    {results.length === 0 && !showRawOption && !searching && (
                      <div className="compose-noresults">
                        No contacts match. Type a full phone number or email address to message
                        someone new.
                      </div>
                    )}
                    {searching && <div className="compose-noresults">Searching…</div>}
                  </div>
                </>
              )}

              {/* The email header, as one block: To, Cc, Bcc and the subject.
                  Cc and Bcc — and every To past the first — ride the MESSAGE,
                  not the thread: the conversation belongs to the picked contact,
                  which is what every list, every assignment and every later
                  reply is keyed on. Cc and Bcc stay folded until asked for, so a
                  window nobody needs them in shows two rows rather than four. */}
              {mailHeader && recipientShape && (
                <>
                  <RecipientFields
                    value={extras}
                    onChange={setExtras}
                    focus={extrasOpen ? 'cc' : 'to'}
                    leadingTo={
                      <span
                        className="cmp-rcpt-chip cmp-rcpt-chip-contact"
                        title={`${recipientShape.name} · ${recipientShape.sub}`}
                      >
                        <ContactAvatar name={recipientShape.name} size={16} />
                        {recipientShape.name}
                        <button type="button" title="Pick someone else" onClick={resetRecipient}>
                          <X size={10} />
                        </button>
                      </span>
                    }
                    subject={
                      caps.subject
                        ? { value: subject, onChange: setSubject, placeholder: 'Subject' }
                        : undefined
                    }
                    {...(extrasOpen ? {} : { hideCopies: true })}
                  />
                  {!extrasOpen && (
                    <button
                      type="button"
                      className="compose-extras-toggle"
                      // On mousedown as WELL as click, and the mousedown is the
                      // one that usually does it. This button sits directly
                      // under the To line, which commits what was typed in it on
                      // blur — so pressing it after typing an address grows that
                      // line by a chip and moves this button out from under the
                      // pointer between press and release, and the click never
                      // arrives. Idempotent, so both firing is fine, and click
                      // is what keeps it reachable from the keyboard.
                      onMouseDown={() => setExtrasOpen(true)}
                      onClick={() => setExtrasOpen(true)}
                    >
                      Cc Bcc
                    </button>
                  )}
                  {copies > 0 && (
                    <div className="compose-hint">
                      Only <b>{recipientShape.name}</b> joins the thread — everyone else gets this
                      one message.
                    </div>
                  )}
                </>
              )}
              {orphanedCopies && (
                <div className="compose-hint compose-hint-warn">
                  {CHANNEL_META[channelKey ?? 'email'].label} takes one recipient and no copies —
                  clear the extra To, Cc and Bcc, or send this by email.
                </div>
              )}
            </div>

            {recipient && channelKey && meta && (
              <div className="compose-section cdock-editor">
                {caps.richText ? (
                  <RichText
                    ref={bodyEditor}
                    value={rich}
                    onChange={setRich}
                    onSubmit={() => void doSend()}
                    disabled={sending}
                    placeholder="Write your email…"
                  />
                ) : (
                  <textarea
                    ref={bodyInput}
                    className="compose-body"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void doSend();
                    }}
                    placeholder={
                      isSms
                        ? 'Write your SMS — keep it under 160 characters to stay one segment.'
                        : `Write your ${meta.label} message…`
                    }
                  />
                )}
              </div>
            )}
          </div>

          <div className="cdock-foot">
            {attachments.length > 0 && (
              <div className="cdock-clips">
                {attachments.map((attachment) => (
                  <span key={attachment.s3Key} className="cmp-attachment">
                    <Paperclip size={11} />
                    <span>{attachment.fileName}</span>
                    <button
                      type="button"
                      title="Remove"
                      onClick={() =>
                        setAttachments((current) =>
                          current.filter((a) => a.s3Key !== attachment.s3Key),
                        )
                      }
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="cdock-foot-row">
              <button
                type="button"
                className="compose-send"
                disabled={!canSend}
                onClick={() => void doSend()}
              >
                <Send size={14} />
                {sending ? 'Sending…' : 'Send'}
              </button>
              {caps.attachments && (
                <>
                  <button
                    type="button"
                    className="cmp-iconbtn"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploading}
                    title={uploading ? 'Uploading…' : 'Attach files'}
                  >
                    <Paperclip size={15} />
                  </button>
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => void pickFiles(e.target.files)}
                  />
                </>
              )}
              {/* Only on a styled channel: a block is html, and the plain-text
                  channels would send the markup rather than the letterhead. */}
              {caps.richText && templateBlocks.length > 0 && (
                <div className="cmp-picker">
                  <button
                    type="button"
                    className="cmp-iconbtn"
                    title="Insert a saved block"
                    onClick={() => setBlocksOpen((open) => !open)}
                  >
                    <FileText size={15} />
                  </button>
                  {blocksOpen && (
                    <div className="cmp-menu" ref={blocksRef}>
                      <div className="cmp-menu-group">Blocks</div>
                      {templateBlocks.map((block) => (
                        <button
                          key={block.id}
                          type="button"
                          title={block.hint || 'Saved in Templates → Components'}
                          // Added to what is written rather than replacing it, so
                          // the caret must survive the press.
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            bodyEditor.current?.insertHtml(block.html);
                            setBlocksOpen(false);
                          }}
                        >
                          <span className="cmp-menu-title">{block.name}</span>
                          <span className="cmp-menu-sub">{block.hint || 'Saved email block'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* The styled editor carries its own tray in the toolbar. */}
              {selectedInbox && !caps.richText && (
                <div className="cmp-emoji-wrap">
                  <button
                    type="button"
                    className="cmp-iconbtn"
                    title="Emoji"
                    onClick={() => setEmojiOpen((open) => !open)}
                  >
                    <Smile size={15} />
                  </button>
                  {emojiOpen && (
                    <div className="rt-emoji cdock-emoji" ref={emojiRef}>
                      {EMOJI.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => insertEmoji(emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ flex: 1 }} />
              {/* Said out loud, because an autosave nobody can see is one nobody
                  trusts — and the whole promise of the Save draft button beside
                  the close is that this window can be walked away from. */}
              {draftState && <span className="cmp-draftstate">{draftState}</span>}
              {count && (
                <span className={`cmp-count${count.over ? ' over' : ''}`}>{count.label}</span>
              )}
              <button type="button" className="cdock-btn" title="Discard" onClick={requestClose}>
                <X size={15} />
              </button>
            </div>
            {(error || uploading) && (
              <div className="cdock-foot-note">
                {error ? <span className="cmp-count over">{error}</span> : 'Uploading…'}
              </div>
            )}
            {!error && !uploading && (
              <div className={`cdock-foot-note${orphanedAttachments || staleInbox ? ' warn' : ''}`}>
                {footerNote}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
